import {
  ChangeEventHandler,
  Fragment,
  useEffect,
  useRef,
  useState,
} from 'react';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import { buildingsLayer, STYLES, STYLES_VALUES_TYPE } from './constants';
import StyleSwitcher from './styleSwitcher';

mapboxgl.accessToken = process.env.NEXT_PUBLIC_MAPBOX_ACCESS_TOKEN ?? '';

interface MapboxMapProps {
  height?: number;
  longitude: string;
  latitude: string;
}

const MapboxMap = ({ height = 500, longitude, latitude }: MapboxMapProps) => {
  const lat = parseFloat(latitude);
  const lng = parseFloat(longitude);

  const map = useRef<mapboxgl.Map | null>(null);
  const mapContainer = useRef<HTMLDivElement>(null);
  const [mapStyle, setMapStyle] = useState<STYLES_VALUES_TYPE>(STYLES.dark);

  useEffect(() => {
    // Ensure the container is rendered first
    if (!mapContainer.current) return;

    const newMap = new mapboxgl.Map({
      container: mapContainer.current,
      style: mapStyle,
      center: [lng, lat],
      zoom: 16,
      pitch: 25,
      bearing: -17.6,
      antialias: true,
    });

    newMap.on('style.load', function () {
      const { layers } = map.current!.getStyle();
      const labelLayer = layers.find(
        (layer) =>
          layer?.type === 'symbol' &&
          layer?.layout &&
          layer.layout['text-field'],
      );
      if (labelLayer && labelLayer.id) {
        map.current!.addLayer(buildingsLayer, labelLayer.id);
      }
    });

    // Create marker
    new mapboxgl.Marker({ color: '#FFFFFF' })
      .setLngLat([lng, lat])
      .addTo(newMap);

    map.current = newMap;

    return () => newMap.remove(); // Clean up on unmount
  }, [lat, lng, mapStyle]);

  const handleRadioChange: ChangeEventHandler<HTMLInputElement> = (
    event,
  ): void => {
    setMapStyle(event.target.value as STYLES_VALUES_TYPE);
  };

  return (
    <Fragment>
      <div ref={mapContainer} style={{ height }} />
      <StyleSwitcher currentSelection={mapStyle} onChange={handleRadioChange} />
    </Fragment>
  );
};

export default MapboxMap;
